import _ from 'underscore';
import { Component, createRef } from 'react';
import { WRModals } from '~/components/wr-modals/wr-modals-container';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import AdminUtils from '../admin-utils';
import { apiDelete, hideLoader, showLoader, handleApiError } from '../../../common';
import AssetPickerGuideSection from './AssetPickerGuideSection';
import AssetPickerEditAssetOptions from './edit-options/asset-picker-edit-asset-options';
import LearningObjectIconTextPair from './LearningObjectIconTextPair';

class AssetPickerGuide extends Component {
  constructor(props, context) {
    super(props, context);
    this.guideRef = createRef();
    let isExpanded = false;

    // Make sure to use .some() because it's way faster than .each()
    // because we don't need to iterate through all tasks if we find a match
    const isCurrentAssetInSection = (currentAsset, section) => {
      // Check if current asset matches section
      if (section.id === currentAsset.id) return true;

      // Check if current asset matches any task in section
      return section.courseSectionTasks?.some((task) => task.id === currentAsset.id) || false;
    };

    const assets = Array.isArray(props.currentAsset) ? props.currentAsset : [props.currentAsset];

    if (props.currentAsset) {
      isExpanded = props.guide.courseSections?.some((section) =>
        assets.some((asset) => isCurrentAssetInSection(asset, section)),
      );
    }

    this.state = {
      isExpanded,
    };
  }

  componentDidMount() {
    // Scroll into view if this guide is active when mounted
    if (this.props.isActive && this.guideRef.current) {
      setTimeout(() => {
        this.scrollIntoView();
      }, 100); // Small delay to ensure DOM is ready
    }
  }

  componentDidUpdate(prevProps) {
    // Scroll into view when guide becomes active
    if (!prevProps.isActive && this.props.isActive && this.guideRef.current) {
      this.scrollIntoView();
    }
    // Also scroll when the timestamp changes and this guide is active
    // This handles the case where the user re-selected the same item to scroll to it again to
    // locate it again in the tree. So we do a neat trick where we update the timestamp to trigger
    // a re-render which effectively causes the tree to scroll to the selected item
    else if (
      this.props.isActive &&
      this.props.currentItemTimestamp &&
      prevProps.currentItemTimestamp !== this.props.currentItemTimestamp &&
      this.guideRef.current
    ) {
      this.scrollIntoView();
    }
  }

  scrollIntoView() {
    // Use scrollIntoView with smooth behavior for better UX
    this.guideRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }

  render() {
    const { isEditable, isActive, guide } = this.props;
    const options = isEditable ? (
      <AssetPickerEditAssetOptions
        assetType="guide"
        renameCallback={this.renameGuide}
        deleteCallback={this.deleteGuide}
      />
    ) : (
      ''
    );
    const moveIcon = isEditable ? (
      <img
        className="asset-picker-move-icon allow-draggable"
        src="/images/move_hover_icon.svg"
        width="9px"
        height="13px"
      />
    ) : (
      ''
    );

    const sections = this.showSections() && this.state.isExpanded ? this.getSections() : '';

    const activeClass = isActive ? 'asset-picker-asset--active' : '';
    const guideTitle = guide.displayTitle;

    return (
      <div
        className={`asset-picker-asset asset-picker-guide ${activeClass}`}
        data-id={guide.id}
        data-type={AssetPickerTypes.GUIDE}
        data-order={guide.order}
        data-parent-id={guide.folderId}
        ref={this.guideRef}
      >
        <div className="asset-picker-asset--header" ref={(guideName) => (this.guideName = guideName)}>
          {moveIcon}
          <div className="asset-picker-asset--label" onClick={this.handleGuideClick}>
            <LearningObjectIconTextPair
              type={AssetPickerTypes.GUIDE}
              isActive={isActive}
              isHidden={guide.isHidden}
              text={guideTitle}
              href={this.props.assetsAreLinks ? AssetPickerTypes.getItemAdminUrl(this.props.guide) : null}
              onPress={this.handleGuideClick}
            />
          </div>
          {options}
        </div>
        {sections}
      </div>
    );
  }

  handleGuideClick = () => {
    const assetCallbacks = this.props.selectableAssets;
    const callback = assetCallbacks[AssetPickerTypes.GUIDE];
    if (callback) callback(this.props.guide);

    this.toggleExpanded();
  };

  toggleExpanded = () => {
    this.setState({
      isExpanded: !this.state.isExpanded,
    });
  };

  showSections = () => this.props.viewableAssets.includes(AssetPickerTypes.GUIDE_SECTION);

  getSections = () => {
    const me = this;
    const sections = this.props.guide.courseSections;
    if (!sections || sections.length === 0) return;

    return (
      <ul className="asset-picker-asset--children asset-picker-guide--sections">
        {_.map(this.props.guide.courseSections, (section) => (
          <AssetPickerGuideSection
            {...me.props}
            key={section.id}
            section={section}
            isActive={me.props.isActiveById[section.id]}
            isActiveById={me.props.isActiveById}
          />
        ))}
      </ul>
    );
  };

  renameGuide = (e) => {
    e.preventDefault();
    const me = this;
    AdminUtils.promptForNewName(me.props.guide, me.props.updateCallback);
  };

  deleteGuide = () => {
    const adminURLPath = AssetPickerTypes.getItemAdminUrl(this.props.guide);
    WRModals.confirm('Are you sure you want to delete this guide?', () => {
      showLoader();
      apiDelete(
        `courses/${this.props.guide.id}`,
        null,
        () => {
          hideLoader();
          this.props.updateCallback();
          if (window.location.pathname === adminURLPath) {
            window.location = '/admin/guides';
          }
        },
        handleApiError(),
      );
    });
  };
}

export default AssetPickerGuide;
